import React, {useEffect, useState} from 'react'
import {graphql} from 'react-apollo'
import * as compose from 'lodash/flowRight'
import {gql} from 'graphql.macro'
import Loading from 'Components/Loading'
import TagClass from 'utils/Tag'
import AdditionalTagClass from 'utils/AdditionalTag'
import {Link} from 'router'
import {connect} from 'react-redux'
import {setDirectoryPath} from 'store/actions'
import {withToolbar} from 'Directory'
// import Modal from 'Components/Modal'
import Graph from './Graph'
import Table from './Table'
import Alarms from './Alarms'
import Settings from './Settings'
import ProductionReportLevel from './ProductionReportLevel'
import ProductionReportTurbine from './ProductionReportTurbine'
// import {route} from 'router'
import toast, {Toaster} from 'react-hot-toast'

import './index.scss'
import ProductionReportFlow from './ProductionReportFlow'

const twoWeeksInMilliseconds = 1209600000
const oneWeekInMilliseconds = twoWeeksInMilliseconds / 2
const GET_DATA_MAX_TIME = Date.now() / 1000 + 15 * 60
const GET_START_TIME = GET_DATA_MAX_TIME - (oneWeekInMilliseconds * 6 / 1000)

const GET_ALL_TAG = gql`
  query GET_ALL_TAG($deviceID: Int!, $tag: String!, $startTime: Float, $endTime: Float, $downsampled: Boolean) {
    deviceReGraph(id: $deviceID, tag: $tag) {
      id
      name
      path
      defaultTag
      defaultTagScale
      firmwareVersion
      groups {
        id
        name
      }
      settings {
        key
        desiredValue
        reportedValue
        reportedTime
      }
      tag(tag: $tag) {
        id
        deviceID
        tag
        ymin
        ymax
        displayUnits
        barrelsPerInch
        reportHour
        filterEnabled
        filterThreshold
        filterMinimumValue
        sensorTempFilterEnabled
        sensorTempFilterThreshold
        temperatureCorrectionEnabled
        temperatureCorrectionCoefficient
        temperatureCorrectionThreshold
        sf6NormalizationEnabled
        fourierPlotEnabled
        maximumFourierAmplitude
        activeAdditional
        averageTempData
        sf6NormalizedDataRe
        additionalTagDataRe(startTime: $startTime, endTime: $endTime, downsampled: $downsampled)
        calibration {
          offset
          fluid
          api
          tds
          time
          target
          tankHeight
          kFactor
          productionOffset
          tankName
          sg
        }
        calibrations {
          offset
          fluid
          api
          tds
          time
          target
          kFactor
          productionOffset
          tankName
          sg
          scada_sensor_type
          valve_high
          valve_low
          gallons_per_inch
        }
        secondaryGraph(startTime: $startTime, endTime: $endTime) {
          deviceID
          tag
          ymin
          ymax
          displayUnits
          data
          filterEnabled
          filterThreshold
          filterMinimumValue
        }
        thirdGraph(startTime: $startTime, endTime: $endTime) {
          deviceID
          tag
          ymin
          ymax
          displayUnits
          data
          filterEnabled
          filterThreshold
          filterMinimumValue
        }
        fourthGraph(startTime: $startTime, endTime: $endTime) {
          deviceID
          tag
          ymin
          ymax
          displayUnits
          data
          filterEnabled
          filterThreshold
          filterMinimumValue
        }
        fifthGraph(startTime: $startTime, endTime: $endTime) {
          deviceID
          tag
          ymin
          ymax
          displayUnits
          data
          filterEnabled
          filterThreshold
          filterMinimumValue
        }
        dataRe(startTime: $startTime, endTime: $endTime, downsampled: $downsampled)
        fourierDataRe(startTime: $startTime, endTime: $endTime)
        userData {
          time
          value
        }
        alarms {
          criticalHigh
          criticalLow
          highWarning
          lowWarning
          unconvertedLowWarning
          unconvertedHighWarning
          unconvertedCriticalLow
          unconvertedCriticalHigh
          flag
          actions {
            delay
            user {
              id
              firstName
              lastName
            }
            action
          }
          rules {
            name
            symbol
            value
            desc
            sent
          }
          log {
            time
            user {
              id
              firstName
              lastName
            }
            action
          }
        }
      }
      comments {
        id
        time
        comment
      }
    }
    me {
      firstName
      lastName
      id
      isSuperUser
      isManager
      canEdit
      company {
        id
      }
      isDemo
    }
  }
`

const ACK_ALARM = gql`
  mutation ACK_ALARM($deviceID: Int!, $tag: String!) {
    ackAlarm(deviceID: $deviceID, tag: $tag) {
      device {
        id
        tags {
          id
          alarms {
            flag
          }
        }
      }
    }
  }
`

const GET_TANK_NAMES = gql`
  query GET_TANK_NAMES {
    tankNames {
      tank_name
    }
  }
`

const tabs = [
  {name: 'Graph', key: 'graph', component: Graph},
  {name: 'Table', key: 'table', component: Table},
  {name: 'Alarms', key: 'alarms', component: Alarms},
  {name: 'Settings', key: 'settings', component: Settings}
]

// Change the name of production report to consumption report for EXXON tank sensors - EXXON REQUESTED CHANGE
const consumptionReportLevelTab = {
  name: 'Consumption Report',
  key: 'levelReport',
  component: ProductionReportLevel
}
const productionReportLevelTab = {
  name: 'Production Report',
  key: 'levelReport',
  component: ProductionReportLevel
}
const productionReportTurbineTab = {
  name: 'Production Report',
  key: 'turbineReport',
  component: ProductionReportTurbine
}
const productionReportFlowTab = {
  name: 'Production Report',
  key: 'flowReport',
  component: ProductionReportFlow
}

const alarmMessages = {
  low: `The sensor triggered a low alarm.`,
  high: `The sensor triggered a high† alarm.`,
  low_warning: `The sensor triggered a low warning alarm.`,
  high_warning: `The sensor triggered a high† warning alarm.`,
  critical_low: `The sensor triggered a critical low alarm.`,
  critical_high: `The sensor triggered a critical high† alarm.`,
  norm: `The sensor's reading returned to normal.`,
  test: `This is a test of the sensor's alarm system.`
}

const Tags = ({data: {loading, error, me, variables, refetch, deviceReGraph}, tank, ackAlarm}) => {
  const [currentTab, setCurrentTab] = useState('graph')
  const [confirming, setConfirming] = useState(false)
  const [device, setDevice] = useState()

  const tanks = tank?.tankNames ? tank?.tankNames.map((t) => t.tank_name) : []

  const Content = tabs
  .concat(productionReportLevelTab) 
  .concat(productionReportTurbineTab)
  .concat(productionReportFlowTab)
  .find((t) => t.key === currentTab).component

  // function used to fetch our tag data from the urls
  // we need them to be in the order they came in
  const fetchData = async (url) => {
    if (url === null) return

    let ret = []
    try {
      ret = await Promise.all(
      url.map(async element => {
        if (!element) return Promise.resolve([])
        return fetch(element).then(res => res.json())
      })
    )
    } catch (err) { console.error(err)}
    return ret
  }

  useEffect(() => {
    if (!deviceReGraph) return

    if (deviceReGraph) {
      setDirectoryPath(deviceReGraph.path)
    } 

    // fetch tag data
    fetchData([deviceReGraph?.tag?.dataRe, deviceReGraph?.tag?.fourierDataRe, deviceReGraph?.tag?.sf6NormalizedDataRe, deviceReGraph?.tag?.additionalTagDataRe])
      .then(res => {
        deviceReGraph.tag.data = res[0]
        deviceReGraph.tag.fourierData = res[1]
        deviceReGraph.tag.sf6NormalizedData = res[2]
        deviceReGraph.tag.additionalTagData = res[3]
      }).finally(() => {
        setDevice(deviceReGraph)
      })

    // second slower query to fetch all data
    // im not sure this is necessary or saves that much more time but im still including it based on previous
    if (deviceReGraph && !variables.downsampled) {
      const refetchPromise = refetch({
        deviceID: variables.deviceID, tag: variables.tag, startTime: GET_START_TIME, endTime: GET_DATA_MAX_TIME, downsampled: true
      }).catch((err) => {
        console.log(err)
        toast.error('Older data temporarily unavailable. Please reload. Contact Sensorfield if this issue persists.', {duration: 100000})
      })

      toast.promise(refetchPromise, {
        loading: 'loading more data...',
        error: 'Something went wrong.'
      }, {
        loading: {position: 'bottom-right'},
        error: {duration: 100000}
      })
    }
    // do not include variables and refetch in dependancy array, they cause too many re-renders
    // disable eslint warning below
    // eslint-disable-next-line
  }, [deviceReGraph])

    // if (loading) return <Loading />
    if (error && !variables.downsampled) return 'Error'
    if (error) console.log(error)
    // this is device not deviceReGraph, device is initialized only after we have fetched tag data from the urls
    // this ensures the components below only load when there is tag data
    if (device && device.tag) {
      const Tag =
        new TagClass(device.tag)
      const Tag2 =
        device.tag.secondaryGraph &&
        new TagClass(device.tag.secondaryGraph)
      const Tag3 =
        device.tag.thirdGraph &&
        new TagClass(device.tag.thirdGraph)
      const Tag4 =
        device.tag.fourthGraph &&
        new TagClass(device.tag.fourthGraph)
      const Tag5 =
        device.tag.fifthGraph &&
        new TagClass(device.tag.fifthGraph)

      let additionalTags = []
      if (Tag?.additionalTagData?.length > 0) {
        Tag.additionalTagData.forEach((dataNode) => {
          additionalTags.push(AdditionalTagClass(dataNode))
        })
      }

      let sf6Tag
      if (device.tag && device.tag.sf6NormalizedData) {
      sf6Tag =
        device?.tag?.sf6NormalizedData.length > 0 &&
        TagClass({
          ...device.tag,
          data: device.tag.sf6NormalizedData
        })
      }

      const childProps = {      
        Tag,
        Tag2,
        Tag3,
        Tag4,
        Tag5,
        additionalTags,
        sf6Tag,
        device,
        tanks,
        me
      }

      return (
        <div className="tag-display">
          <Toaster
            toastOptions={{
              className: '',
              style: {
                border: '1px solid blue',
              }
            }}
          />
          <div className="tag-display-header">
            <h1>
              <Link path={`/devices/${device.id}`}>{device.name}</Link>
            </h1>
            <i className="fa fa-angle-right" />
            <span className="tag-label">{Tag.label}</span>
            <span className="edit-device-link">
              <div className="device-id">ID: {device.id}</div>
              <Link path={`/devices/${device.id}`}>Device Info</Link>
            </span>
          </div>
          {Tag?.alarms?.rules.filter((i) => i.sent !== 0 && i.sent !== null).length !== 0 && (
          <div> 
            <div className={'open-alarm high'}>
              <span className="text">{`The sensor triggered an alarm rule. View the alarm tab for more detail.`}</span>
            </div>
          </div>
          )}
          {Tag.alarms.flag && (
            <div className={'open-alarm ' + Tag.alarms.flag}>
              <span className="text">{alarmMessages[Tag.alarms.flag]}</span>
              {confirming ? (
                <span className="buttons">
                  <button onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                  <button
                    className="primary"
                    onClick={() => {
                      const updatePromise = ackAlarm()
                      toast.promise(updatePromise, {
                        loading: 'Loading...',
                        success: () => {      
                          return ('Done')
                        },
                        error: 'Something Went Wrong',
                      })
                      setConfirming(false)
                    }}
                  >
                    Confirm
                  </button>
                </span>
              ) : (
                <span className="buttons">
                  <button
                    className="primary"
                    onClick={() => setConfirming(true)}
                  >
                    Acknowledge
                  </button>
                </span>
              )}
            </div>
          )}
          {Tag.tag === 'spsr' && Tag.calibrations.length === 0 ? (
            <div className={'open-alarm cal'}>
            <span className="text">This sensor has not been calibrated and will not give reliable 
              To calibrate the sensor, click on a point on the graph and choose
              the "Add Calibration" option.</span>
            </div>
          ) : null}
          {Tag.tag === 'dspsr' && Tag.calibrations.length === 0 ? (
            <div className={'open-alarm cal'}>
            <span className="text">This sensor has not been calibrated and will not give reliable 
              To calibrate the sensor, click on a point on the graph and choose
              the "Add Calibration" option.</span>
            </div>
          ) : null}
          <div className="tabs">
            {( device.tag.calibration.tankName != null && (Tag.tag === 'spsr' || Tag.tag === 'dspsr')
              ? tabs.concat(consumptionReportLevelTab)
              : Tag.tag === 'spsr' || Tag.tag === 'dspsr'
              ? tabs.concat(productionReportLevelTab)
              : Tag.tag === 'puls'
              ? tabs.concat(productionReportTurbineTab)
              : Tag.tag === 'flw'
              ? tabs.concat(productionReportFlowTab)
              : tabs
            ).map((t) => (
              <button
                key={t.key}
                className={currentTab.key === t.key ? 'tab active' : 'tab'}
                onClick={() => setCurrentTab(t.key)}
              >
                {t.name}
              </button>
            ))}
          </div>
          <div className="content">
            <Content {...childProps} />
          </div>
        </div>
      )
    }

    // unconditional return needed
    return (<Loading />)
  }


const mapStateToProps = () => ({})
const mapDispatchToProps = (dispatch) => ({
  setDirectoryPath: (path) => dispatch(setDirectoryPath(path))
})

export default compose(
  graphql(GET_ALL_TAG, {
    options: (props) => ({
      variables: {deviceID: props.deviceID, tag: props.tag, startTime: GET_START_TIME, endTime: GET_DATA_MAX_TIME},
      skip: (props) => !props.deviceID,
    })
  }),
  graphql(ACK_ALARM, {
    name: 'ackAlarm',
    options: (props) => ({
      variables: {deviceID: props.deviceID, tag: props.tag},
      skip: (props) => !props.deviceID
    })
  }),
  graphql(GET_TANK_NAMES, {
    name: 'tank',
    skip: (props) => !props.tag

  }),
  connect(mapStateToProps, mapDispatchToProps),
  withToolbar
)(Tags)